/**
 * E018-SPK021: Page Mode Filter Utility
 *
 * Centralized logic to determine if a canvas block should be hidden in page mode.
 * Used by: Database, Automation, Ghost blocks.
 *
 * Canvas blocks (with xywh coordinates) should NOT appear in page mode.
 * Blocks inside EdgelessOnly notes should also be hidden in page mode.
 */

import { type NoteBlockModel, NoteDisplayMode } from '@blocksuite/affine-model';
import { DocModeProvider } from '@blocksuite/affine-shared/services';
import type { BlockComponent } from '@blocksuite/std';

/**
 * Check if a block should render in the current document mode.
 *
 * Returns FALSE (should hide) when:
 * 1. In page mode AND block has xywh coordinates (canvas block)
 * 2. In page mode AND parent note has EdgelessOnly displayMode
 *
 * @param component - The block component to check
 * @returns true if block should render, false if it should be hidden
 */
export function shouldRenderInPageMode(component: BlockComponent): boolean {
  const docMode = component.std.getOptional(DocModeProvider)?.getEditorMode();

  // In edgeless mode, always render
  if (docMode !== 'page') return true;

  const store = component.store;
  const model = component.model;
  const parent = store.getParent(model.id);

  // Case 1: Block directly under @root (canvas-created)
  // These have xywh coordinates and should NOT appear in page mode
  if (!parent || parent.flavour === 'affine:page') {
    const xywh = (model.props as Record<string, unknown>).xywh;
    if (xywh) {
      return false; // Canvas block with position → hide in page mode
    }
    return true; // No xywh → probably a doc-mode block
  }

  // Case 2: Block under a Note - check note's displayMode
  if (parent.flavour === 'affine:note') {
    const displayMode = (parent as NoteBlockModel).props.displayMode;
    if (displayMode === NoteDisplayMode.EdgelessOnly) {
      return false; // EdgelessOnly note → hide in page mode
    }
  }

  return true;
}

/**
 * Apply page mode filtering to a block component.
 * Call this in connectedCallback() to hide canvas blocks in page mode.
 *
 * @param component - The block component
 * @returns true if component should continue rendering, false if hidden
 */
export function applyPageModeFilter(component: BlockComponent): boolean {
  if (!shouldRenderInPageMode(component)) {
    component.style.display = 'none';
    return false;
  }
  return true;
}
